import { useEffect, useState } from "react";
import { Clock, Smartphone, Tablet, Monitor, ShieldAlert, ShieldCheck, MapPin, KeyRound, Fingerprint, Globe } from "lucide-react";
import { getLoginHistory } from "@/lib/api";

const DeviceIcon = ({ kind, className = "h-4 w-4" }) => {
  if (kind === "mobile") return <Smartphone className={className} />;
  if (kind === "tablet") return <Tablet className={className} />;
  return <Monitor className={className} />;
};

const MethodBadge = ({ method }) => {
  if (method === "google") return (
    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
      <Globe className="h-2.5 w-2.5" /> Google
    </span>
  );
  if (method === "passkey") return (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
      <Fingerprint className="h-2.5 w-2.5" /> Passkey
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-secondary text-foreground/80 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
      <KeyRound className="h-2.5 w-2.5" /> Password
    </span>
  );
};

const _formatTs = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const mins = Math.floor((now - d) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    if (mins < 60 * 24) return `${Math.floor(mins / 60)}h ago`;
    if (mins < 60 * 24 * 7) return `${Math.floor(mins / (60 * 24))}d ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
};

/**
 * Shows the last 10 sign-in attempts (success + failure) so users can spot
 * suspicious activity. Any row where the country differs from the most-recent
 * successful login gets a "New location" amber warning.
 */
export const LoginHistoryCard = () => {
  const [events, setEvents] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    getLoginHistory(10)
      .then((r) => setEvents(r.events || []))
      .catch(() => setError("Couldn't load sign-in history."));
  }, []);

  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid="login-history-card">
      <div className="flex items-center gap-2 mb-1">
        <Clock className="h-5 w-5 text-accent" />
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Sign-in history</span>
      </div>
      <h3 className="font-serif text-2xl font-bold">Recent activity</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Last 10 sign-in attempts on this account. Something you don't recognise?
        <span className="ml-1 font-semibold text-foreground">Change your password immediately.</span>
      </p>

      {error && <p className="mt-4 text-sm text-destructive">{error}</p>}

      {events === null && (
        <div className="mt-5 space-y-2 animate-pulse">
          {[0, 1, 2].map((i) => <div key={i} className="h-14 rounded-xl bg-secondary/60" />)}
        </div>
      )}

      {events && events.length === 0 && (
        <p className="mt-6 text-sm text-muted-foreground">No sign-in events yet. Try signing out and back in — your next login will appear here.</p>
      )}

      {events && events.length > 0 && (
        <ul className="mt-5 divide-y divide-border rounded-xl border border-border overflow-hidden" data-testid="login-history-list">
          {events.map((e) => (
            <li
              key={e.id}
              data-testid={`login-history-row-${e.id}`}
              className={`flex items-start gap-3 px-4 py-3 text-sm ${e.success ? "" : "bg-destructive/5"}`}
            >
              <span className={`grid place-items-center h-9 w-9 rounded-lg shrink-0 ${e.success ? "bg-accent/10 text-accent" : "bg-destructive/10 text-destructive"}`}>
                {e.success ? <DeviceIcon kind={e.device} /> : <ShieldAlert className="h-4 w-4" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold truncate">{e.browser || "Unknown"} on {e.os || "Unknown OS"}</span>
                  <MethodBadge method={e.method} />
                  {!e.success && (
                    <span className="rounded-full bg-destructive/15 text-destructive text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                      Failed
                    </span>
                  )}
                  {e.new_location && e.success && (
                    <span data-testid={`new-location-flag-${e.id}`} className="rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                      ⚠️ New location
                    </span>
                  )}
                </div>
                <div className="mt-1 text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {[e.city, e.country].filter(Boolean).join(", ") || (e.ip || "Unknown location")}
                  </span>
                  <span>{_formatTs(e.ts)}</span>
                </div>
              </div>
              {e.success && (
                <ShieldCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-1" aria-label="Successful" />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
